import {
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { FaBell, FaUser } from "react-icons/fa";
import { RiMenu2Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { showSuccessToast, showErrorToast } from "../../utils/toaster";
import { baseurl } from "../../config/url";
import CloseIcon from "@mui/icons-material/Close";

// Function to format MongoDB date into a readable format
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const isValidMobile = (mobile) => {
  const re = /^[6-9]\d{9}$/;
  return re.test(mobile);
};

const isValidGST = (gst) => {
  const re = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return re.test(gst);
};

const isValidPAN = (pan) => {
  const re = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return re.test(pan);
};



// Base URL for API requests
// const baseurl = "http://localhost:5000"; 
const Header = ({ setCollapsed, collapsed }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [sellerDetails, setSellerDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalDetails, setOriginalDetails] = useState({});



  const [msgAnchorEl, setMsgAnchorEl] = useState(null);
  const openMsg = Boolean(msgAnchorEl);

  const handleMsgClick = (event) => setMsgAnchorEl(event.currentTarget);
  const handleMsgClose = () => setMsgAnchorEl(null);


  // Mock Seller ID (Replace with the actual seller ID from auth or localStorage)
  const sellerId = localStorage.getItem("sellerId");

  const [notifications, setNotifications] = useState([]);
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${baseurl}/api/notification/${sellerId}`);
      console.log("Fetched Notifications:", response.data);
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showErrorToast("Failed to fetch notifications");
    }
  };
  useEffect(() => {

    if (sellerId) {
      fetchNotifications(sellerId);
    } else {
      console.warn("Seller ID is missing or invalid.");
    }
  }, [sellerId]);


  // Open/Close Menu
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // Open Sidebar
  const handleProfileClick = () => {
    if (!sellerId) {
      showErrorToast("Seller ID not found. Please log in again.");
      navigate("/login");
      return;
    }
    setIsEditing(false);
    setSidebarOpen(true);
    fetchSellerDetails();
    handleClose();
  };
  // Close Sidebar
  const toggleSidebar = (open) => () => setSidebarOpen(open);

  // Fetch Seller Details
  const fetchSellerDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${baseurl}/api/seller/seller-by-id/${sellerId}`);
      setSellerDetails(response.data);

      if (!isEditing) {
        setOriginalDetails(response.data); // only reset if not editing
      }
    } catch (error) {
      showErrorToast("Failed to fetch seller details");
    } finally {
      setIsLoading(false);
    }
  };

  // Call fetchSellerDetails when component mounts
  useEffect(() => {
    if (sellerId) {
      fetchSellerDetails();
    }
  }, [sellerId]); // Dependency array ensures it runs only when sellerId changes

  // Update Seller Details
  const handleUpdate = async () => {
    const current = JSON.stringify(sellerDetails);
    const original = JSON.stringify(originalDetails);

    if (current === original) {
      showErrorToast("No changes detected");
      return;
    }

    // validate email if it is being edited
    if (!isValidEmail(sellerDetails.email)) {
      showErrorToast("Please enter a valid email address");
      return;
    }


    if (!sellerDetails.mobile || !isValidMobile(sellerDetails.mobile)) {
      showErrorToast("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!sellerDetails.gstNumber || !isValidGST(sellerDetails.gstNumber)) {
      showErrorToast("Please enter a valid GST number");
      return;
    }

    if (!sellerDetails.pan || !isValidPAN(sellerDetails.pan)) {
      showErrorToast("Please enter a valid PAN number");
      return;
    }

    try {
      const response = await axios.put(
        `${baseurl}/api/seller/updateSeller/${sellerId}`,
        sellerDetails
      );
      showSuccessToast(response.data.message);


      // re-fetch fresh data
      await fetchSellerDetails();

      setIsEditing(false);
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update details");
    }
  };


  // Handle Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSellerDetails((prevState) => ({ ...prevState, [name]: value }));
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
    showSuccessToast("You have been logged out successfully.");
  };

  const handleUpdatePage = () => {
    navigate("/update-profile");
  };

  useEffect(() => {
    fetchSellerDetails();
  }, []);

  return (
    <>
      <div className="bg-gray-400 text-white p-2 px-3 flex items-center justify-between shadow-lg sticky top-0 w-full z-50">
        <div onClick={() => setCollapsed(!collapsed)} className="cursor-pointer">
          <RiMenu2Line className="text-2xl" />
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <FaBell
              className="text-2xl cursor-pointer hover:text-yellow-400"
              onClick={handleMsgClick}
            />
            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {notifications.filter(n => !n.isRead).length}
            </span>

            <Menu anchorEl={msgAnchorEl} open={openMsg} onClose={handleMsgClose}>
              {notifications.length === 0 ? (
                <MenuItem disabled>No notifications</MenuItem>
              ) : (
                notifications.map((notif) => (
                  <MenuItem
                    key={notif._id}
                    onClick={async () => {
                      try {
                        await axios.put(`${baseurl}/api/notification/read/${notif._id}`);
                        fetchNotifications(sellerId);
                        handleMsgClose();
                      } catch (error) {
                        console.log("Seller ID from localStorage:", sellerId);
                        showErrorToast("Faisled to update notification.");
                      }
                    }}
                  >
                    {notif.message}
                  </MenuItem>
                ))
              )}
            </Menu>
          </div>

          <div>
            <IconButton onClick={handleClick}>
              <FaUser className="text-2xl cursor-pointer hover:text-black text-white" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
              <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      <Drawer
        anchor="right"
        open={isSidebarOpen}
        onClose={toggleSidebar(false)}
        PaperProps={{
          style: {
            width: "600px",
            padding: "16px",
          },
        }}
      >
        {/* Cross Button at the Top */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "16px" }}>User Profile</h2>
          <IconButton onClick={toggleSidebar(false)}>
            <CloseIcon />
          </IconButton>
        </div>


        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <List>
            <Grid container spacing={2}>
              {/* Two-column layout */}

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={sellerDetails.name || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Name"
                      secondary={sellerDetails.name || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={sellerDetails.email || ""}
                    onChange={handleInputChange}
                    error={sellerDetails.email && !isValidEmail(sellerDetails.email)}
                    helperText={
                      sellerDetails.email && !isValidEmail(sellerDetails.email)
                        ? "Invalid email format"
                        : ""
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={sellerDetails.email || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>


              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Mobile"
                    name="mobile"
                    value={sellerDetails.mobile || ""}
                    onChange={handleInputChange}
                    error={isEditing && (!sellerDetails.mobile || !isValidMobile(sellerDetails.mobile))}
                    helperText={
                      isEditing && (!sellerDetails.mobile || !isValidMobile(sellerDetails.mobile))
                        ? "Enter a valid 10-digit mobile number"
                        : ""
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Mobile"
                      secondary={sellerDetails.mobile || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>


              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={sellerDetails.companyName || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Company Name"
                      secondary={sellerDetails.companyName || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="GST Number"
                    name="gstNumber"
                    value={sellerDetails.gstNumber || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="GST Number"
                      secondary={sellerDetails.gstNumber || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="PAN Number"
                    name="pan"
                    value={sellerDetails.pan || ""}
                    onChange={handleInputChange}
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="PAN Number"
                      secondary={sellerDetails.pan || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Street"
                    name="street"
                    value={sellerDetails.address?.street || ""}
                    onChange={(e) =>
                      setSellerDetails((prev) => ({
                        ...prev,
                        address: { ...prev.address, street: e.target.value },
                      }))
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Street"
                      secondary={sellerDetails.address?.street || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={sellerDetails.address?.city || ""}
                    onChange={(e) =>
                      setSellerDetails((prev) => ({
                        ...prev,
                        address: { ...prev.address, city: e.target.value },
                      }))
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="City"
                      secondary={sellerDetails.address?.city || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={sellerDetails.address?.state || ""}
                    onChange={(e) =>
                      setSellerDetails((prev) => ({
                        ...prev,
                        address: { ...prev.address, state: e.target.value },
                      }))
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="State"
                      secondary={sellerDetails.address?.state || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={sellerDetails.address?.country || ""}
                    onChange={(e) =>
                      setSellerDetails((prev) => ({
                        ...prev,
                        address: { ...prev.address, country: e.target.value },
                      }))
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Country"
                      secondary={sellerDetails.address?.country || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>

              <Grid item xs={6}>
                {isEditing ? (
                  <TextField
                    fullWidth
                    label="Postal Code"
                    name="postalCode"
                    value={sellerDetails.address?.postalCode || ""}
                    onChange={(e) =>
                      setSellerDetails((prev) => ({
                        ...prev,
                        address: { ...prev.address, postalCode: e.target.value },
                      }))
                    }
                  />
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="Postal Code"
                      secondary={sellerDetails.address?.postalCode || "N/A"}
                    />
                  </ListItem>
                )}
              </Grid>


              {/* 
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Registration Date"
                  value={formatDate(sellerDetails.createdAt)}
                  disabled
                />
              </Grid> */}

            </Grid>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (isEditing) {
                  handleUpdate();    // save
                }
                setIsEditing(!isEditing);  // toggle
              }}
              disabled={
                isEditing &&
                (
                  !isValidEmail(sellerDetails.email) ||
                  !sellerDetails.mobile ||
                  !isValidMobile(sellerDetails.mobile)
                )
              }
              style={{ marginTop: "16px", fontSize: "0.9rem" }}
            >
              {isEditing ? "Save Profile" : "Edit Profile"}
            </Button>



          </List>
        )}
      </Drawer>

    </>
  );
};

export default Header;
